import { useState, UIEvent, ChangeEvent } from "react";
import { MemeGeneratorState, TextBox } from "store/features/memeGenerator/types";
import { FontSources } from "store/features/memeGenerator/types/recentlyUsedFont";
import { store, useAppDispatch } from "store/store";
import { useRecentlyUsedFonts } from "../useRecentlyUsedFonts";
import { setLoaded, setSettings } from "store/features/memeGenerator/actions";
import { addToRecentlyUsedFonts } from "store/features/memeGenerator/utils/addToRecentlyUsedFonts";
import { appendToRecentlyUsedFonts } from "store/features/memeGenerator/actions";
import { webFonts as webFontsDefaultList } from "store/features/memeGenerator/data/webFonts";
import { loadFont } from "store/utils/loadFont";
import { useGoogleAnalytics } from "client/hooks/utils/useGoogleAnalytics";

export const useFontList = (textBox: TextBox, loadMoreFonts: () => Promise<void>, onClose: () => void) => {
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [webFonts, setWebFonts] = useState(webFontsDefaultList);
    const [searchedGoogleFonts, setSearchedGoogleFonts] = useState<MemeGeneratorState["googleFonts"]["list"]>([]);
    const [isLoadingSearchedGoogleFonts, setIsLoadingSearchedGoogleFonts] = useState(false);
    const [search, setSearch] = useState("");
    const dispatch = useAppDispatch();
    const { recentlyUsedFonts: _recentlyUsedFonts, isLoadingRecentlyUsedFonts } = useRecentlyUsedFonts();
    const recentlyUsedFonts = _recentlyUsedFonts.filter((_f) => _f.family !== textBox.fontFamily);

    async function handleScroll(e: UIEvent) {
        if (search) return;
        const el = e.target as HTMLElement;
        if (el.scrollHeight - el.clientHeight - el.scrollTop < 1) {
            if (isLoadingMore) return;
            setIsLoadingMore(true);
            await loadMoreFonts();
            setIsLoadingMore(false);
        }
    }
    const { gtag } = useGoogleAnalytics();

    function handleClick(family: string, source: FontSources) {
        dispatch(
            setSettings({
                id: textBox.id,
                fontFamily: family,
            }),
        );
        addToRecentlyUsedFonts({
            family,
            source,
        });
        dispatch(
            appendToRecentlyUsedFonts({
                family,
                source,
            }),
        );
        if (source === FontSources.WEBFONT) {
            gtag("event", "meme_web_font_selected", {
                page: "meme generator",
                meme_font: family,
            });
        } else {
            gtag("event", "meme_google_font_selected", {
                page: "meme generator",
                google_font: family,
            });
        }
        onClose();
    }

    async function handleSearch(e: ChangeEvent<HTMLInputElement>) {
        setSearch(e.target.value);
        const search = e.target.value.toLowerCase();
        if (!search) {
            setWebFonts(webFontsDefaultList);
            return;
        }
        setWebFonts(search ? webFontsDefaultList.filter((f) => f.toLowerCase().includes(search)) : webFontsDefaultList);
        const filteredGoogleFonts = store
            .getState()
            .memeGenerator.googleFonts.list.filter(
                (f) => f.family.toLowerCase().includes(search) && f.family !== "Source Sans Pro",
            );
        if (filteredGoogleFonts.length <= 0) return;
        setSearchedGoogleFonts(filteredGoogleFonts);
        setIsLoadingSearchedGoogleFonts(true);
        const toBeLoadedFonts = filteredGoogleFonts.filter((f) => !f.hasLoaded);
        await Promise.all(
            toBeLoadedFonts
                .map((f) =>
                    f.variants.filter((v) => v === "regular").map((v) => ({ family: f.family, url: f.files[v] })),
                )
                .flat()
                .map((f) => loadFont(f.family, f.url)),
        );
        toBeLoadedFonts.forEach((f) => dispatch(setLoaded(f.family)));
        setIsLoadingSearchedGoogleFonts(false);
    }

    return {
        handleScroll,
        recentlyUsedFonts,
        isLoadingRecentlyUsedFonts,
        handleClick,
        isLoadingMore,
        webFonts,
        search,
        handleSearch,
        searchedGoogleFonts,
        isLoadingSearchedGoogleFonts,
    };
};
